// Responses & revisions: every answer, query or revision in the back and forth — logged in place, moves the onus.
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { fmtDate } from '../../lib/format';
import { Button, errText, useToast } from '../kit';
import { useViewer } from '../Viewer';
import { fileTag } from '../../lib/fileType';
import { orgShort, today, type Org, type Person } from './shared';
import { shortDate, type Response } from './Workflow';
import { C3Busy } from '../C3Loader';

const VIA = ['email', 'letter', 'meeting', 'site', 'phone', 'WhatsApp'];
const REQ = [
  { key: 'response', label: 'Response received', kind: 'response' },
  { key: 'revised', label: 'Revised claim', kind: 'request' },
  { key: 'docs', label: 'More documents', kind: 'request' },
  { key: 'proof', label: 'Proof / photos', kind: 'request' },
  { key: 'note', label: 'Note', kind: 'note' },
];

export type ResponsesHandle = { open: (final?: boolean, opts?: { from?: string; onus?: string }) => void };

export const Responses = forwardRef<
  ResponsesHandle,
  {
    itemId: string;
    siteId: string;
    responses: Response[];
    orgs: Map<string, Org>;
    allOrgs: Org[];
    people: Map<string, Person>;
    defaultFrom: string | undefined;
    defaultOnus: string | undefined;
    finalLabel: string | null;
    /** claims/variations: log requests to PBS (revised claim, more documents, proof) as well as responses */
    requests?: boolean;
    canEdit: boolean;
    onSaved: (r: { final: boolean; batch: string; from: string | null }) => Promise<void> | void;
    onFiles: () => void;
    /** the item's files — documents attached to a response show under it (V0.4.10) */
    files?: { id: string; original_name: string; mime: string | null; kind: string; response_id?: string | null; deleted_at: string | null }[];
  }
>(function Responses({ itemId, siteId, responses, orgs, allOrgs, people, defaultFrom, defaultOnus, finalLabel, requests, canEdit, onSaved, onFiles, files: itemFiles = [] }, ref) {
  const viewer = useViewer();
  const pick = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [body, setBody] = useState('');
  const [from, setFrom] = useState(defaultFrom ?? '');
  const [onus, setOnus] = useState<string>(defaultOnus ?? 'keep');
  const [via, setVia] = useState('email');
  const [on, setOn] = useState(today());
  const [interim, setInterim] = useState(false);
  const [final, setFinal] = useState(false);
  const [rtype, setRtype] = useState<string>('response');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const swiOrg = allOrgs.find((o) => o.short_code === 'SWI')?.id;
  const contractorOrg = allOrgs.find((o) => o.short_code === 'PBS')?.id;
  const box = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    open: (f = false, opts) => {
      setAdding(true);
      setFinal(f);
      setFrom(opts?.from ?? defaultFrom ?? '');
      setOnus(opts?.onus ?? (f ? 'none' : (defaultOnus ?? 'keep')));
      requestAnimationFrame(() => {
        box.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ta.current?.focus();
      });
    },
  }));

  const list = [...responses].filter((r) => !r.deleted_at).sort((a, b) => (a.received_on ?? a.created_at).localeCompare(b.received_on ?? b.created_at));
  const numbered = list.map((r, i) => ({ r, n: i }));

  async function save() {
    if (!body.trim()) return;
    setBusy(true);
    const batch = crypto.randomUUID();
    try {
      const sets = onus !== 'keep';
      const row = await write<{ id: string }>('item_responses', 'insert', {
        batch_id: batch,
        values: {
          item_id: itemId,
          kind: rtype === 'response' ? 'response' : rtype === 'note' ? 'note' : 'request',
          request_type: REQ.find((r) => r.key === rtype && r.kind === 'request')?.label ?? null,
          from_org_id: from || null,
          body: body.trim(),
          via: via || null,
          received_on: on || null,
          is_interim: interim,
          is_final: final,
          sets_onus: sets,
          onus_org_id: sets && onus !== 'none' ? onus : null,
        },
        summary: `Logged ${rtype === 'response' ? 'response' : rtype === 'note' ? 'note' : 'request'} R${list.length}${from ? ` from ${orgShort(orgs.get(from))}` : ''}`,
      });
      for (const f of files) {
        try {
          await uploadFile(f, { siteId, linkedTable: 'items', linkedId: itemId, fromOrgId: from || null, responseId: row.id });
        } catch (e) {
          toast({ tone: 'error', text: `${f.name}: ${errText(e)}` });
        }
      }
      if (files.length) onFiles();
      await onSaved({ final, batch, from: from || null });
      setBody('');
      setFiles([]);
      setInterim(false);
      setFinal(false);
      setAdding(false);
      toast({ tone: 'ok', text: 'Response saved' });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {numbered.length === 0 ? <p className="muted small" style={{ margin: '0 0 8px' }}>No responses logged yet.</p> : null}
      {[...numbered].reverse().map(({ r, n }) => {
        const by = r.created_by ? people.get(r.created_by) : undefined;
        return (
          <div className="resp" key={r.id}>
            <span className={`face ${r.from_org_id && orgs.get(r.from_org_id)?.kind === 'internal' ? '' : 'ext'}`} aria-hidden="true">
              {r.from_org_id ? (orgs.get(r.from_org_id)?.short_code ?? orgShort(orgs.get(r.from_org_id))).slice(0, 4) : '—'}
            </span>
            <div className="resp-body">
              <div className="resp-head">
                <span className="rev">R{n}</span>
                <strong>
                  {r.from_org_id ? orgs.get(r.from_org_id)?.name : 'Note'}
                  {r.sets_onus ? ` → ${r.onus_org_id ? orgShort(orgs.get(r.onus_org_id)) : 'answered'}` : ''}
                </strong>
                <span className="muted">
                  {r.via ? `· by ${r.via} ` : ''}· {r.received_on ? fmtDate(r.received_on) : shortDate(r.created_at)}
                </span>
                {r.kind === 'request' ? <span className="chip-s req">Request{r.request_type ? `: ${r.request_type.toLowerCase()}` : ''}</span> : null}
                {r.is_interim ? <span className="chip-s">Interim</span> : null}
                {r.is_final ? <span className="chip-s good">Final</span> : null}
              </div>
              <p className="cmt-text">{r.body}</p>
              {itemFiles.some((f) => f.response_id === r.id && !f.deleted_at) ? (
                <div className="resp-files">
                  {itemFiles
                    .filter((f) => f.response_id === r.id && !f.deleted_at)
                    .map((f) => (
                      <button key={f.id} type="button" className="resp-file" onClick={() => viewer({ id: f.id, name: f.original_name, mime: f.mime, kind: f.kind })}>
                        <span className={`ext ${fileTag(f.original_name, f.kind).cls}`}>{fileTag(f.original_name, f.kind).label}</span>
                        {f.original_name}
                      </button>
                    ))}
                </div>
              ) : null}
              {by ? <div className="muted small">Logged by {by.full_name || by.email}</div> : null}
            </div>
          </div>
        );
      })}
      {canEdit ? (
        adding ? (
          <div
            ref={box}
            className={`resp-form ${over ? 'over' : ''}`}
            onDragOver={(e) => (e.preventDefault(), setOver(true))}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              setFiles([...files, ...Array.from(e.dataTransfer.files)]);
            }}
          >
            {requests ? (
              <div className="chips" role="radiogroup" aria-label="What kind of entry">
                {REQ.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    role="radio"
                    aria-checked={rtype === r.key}
                    className={`chip ${rtype === r.key ? 'on' : ''}`}
                    onClick={() => {
                      setRtype(r.key);
                      if (r.kind === 'request') {
                        setFrom(swiOrg ?? '');
                        setOnus(contractorOrg ?? 'keep');
                      }
                    }}
                  >
                    {r.kind === 'request' ? `Request: ${r.label.toLowerCase()}` : r.label}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="field">
              <span className="field-label">{rtype === 'response' ? 'New response' : rtype === 'note' ? 'Note' : 'Request'} R{list.length}</span>
              <textarea ref={ta} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Paste or type the answer, query or revision — drop the drawing or letter here" rows={4} />
            </label>
            <div className="resp-row">
              <label>
                From
                <select value={from} onChange={(e) => setFrom(e.target.value)}>
                  <option value="">—</option>
                  {allOrgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Onus now on
                <select value={onus} onChange={(e) => setOnus(e.target.value)}>
                  <option value="keep">No change</option>
                  <option value="none">No one — answered</option>
                  {allOrgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Received
                <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
              </label>
              <label>
                By
                <select value={via} onChange={(e) => setVia(e.target.value)}>
                  {VIA.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="resp-attach">
              <Button kind="ghost" onClick={() => pick.current?.click()}>
                + Attach a document
              </Button>
              <input ref={pick} type="file" multiple hidden aria-label="Attach a document to this response" onChange={(e) => (e.target.files && setFiles([...files, ...Array.from(e.target.files)]), (e.target.value = ''))} />
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} className="sw-chip">
                  {f.name}
                  <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </span>
              ))}
              {!files.length ? <span className="small muted">or drop files here — they stay with this response and go into the item's Files</span> : null}
            </div>
            <div className="resp-row">
              <label className="check">
                <input type="checkbox" checked={interim} onChange={(e) => setInterim(e.target.checked)} /> Interim
              </label>
              {finalLabel ? (
                <label className="check">
                  <input
                    type="checkbox"
                    checked={final}
                    onChange={(e) => {
                      setFinal(e.target.checked);
                      if (e.target.checked && onus === 'keep') setOnus('none');
                    }}
                  />{' '}
                  {finalLabel}
                </label>
              ) : null}
            </div>
            <div className="resp-row">
              <Button kind="primary" onClick={() => void save()} disabled={busy || !body.trim()}>
                {busy ? <C3Busy text="Saving…" accent /> : rtype === 'response' ? 'Save response' : rtype === 'note' ? 'Save note' : 'Save request'}
              </Button>
              <Button onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button kind="ghost" onClick={() => (setAdding(true), setFrom(defaultFrom ?? ''), setOnus(defaultOnus ?? 'keep'))}>
            + Add response
          </Button>
        )
      ) : null}
    </div>
  );
});
